import { useRef, useState, type KeyboardEvent } from 'react'
import { CheckCircle2, ShieldX } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Label } from '@/components/ui/Label'
import {
  useApproveBlock,
  useRejectBlock,
} from '../hooks/useBlockMutations'

interface Props {
  blockId: string
  blockNumber: string
  /** "approve" pre-selects the approve action; "reject" pre-selects reject. */
  defaultMode: 'approve' | 'reject'
  open: boolean
  onOpenChange: (open: boolean) => void
  onResolved: () => void
}

const REJECT_REASON_MIN = 10

/**
 * AF-128 — combined approve/reject dialog. Reject requires a reason captured
 * for audit. Approve is a single-click confirmation. Self-approve is blocked
 * upstream — by the time this dialog renders, the parent has verified the
 * current user is not the requester.
 */
export function ApproveRejectDialog({
  blockId,
  blockNumber,
  defaultMode,
  open,
  onOpenChange,
  onResolved,
}: Props) {
  const [mode, setMode] = useState<'approve' | 'reject'>(defaultMode)
  const [reason, setReason] = useState('')

  const approve = useApproveBlock()
  const reject = useRejectBlock()
  const pending = approve.isPending || reject.isPending

  const submit = () => {
    if (mode === 'approve') {
      approve.mutate(
        { block_id: blockId },
        {
          onSuccess: () => {
            onOpenChange(false)
            onResolved()
            setReason('')
          },
        },
      )
    } else {
      if (reason.trim().length < REJECT_REASON_MIN) return
      reject.mutate(
        { block_id: blockId, reason: reason.trim() },
        {
          onSuccess: () => {
            onOpenChange(false)
            onResolved()
            setReason('')
          },
        },
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'approve' ? 'Approve block' : 'Reject block'} {blockNumber}
          </DialogTitle>
          <DialogDescription>
            {mode === 'approve'
              ? 'Approving makes the block active immediately. Recorded in the audit trail.'
              : 'Rejection sends the request back to the requester with your reason.'}
          </DialogDescription>
        </DialogHeader>

        <ModeTabs mode={mode} onModeChange={setMode} />

        <div
          role="tabpanel"
          id="approve-reject-panel"
          aria-labelledby={mode === 'approve' ? 'tab-approve' : 'tab-reject'}
        >
          {mode === 'reject' && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="reject-reason">Reason</Label>
              <textarea
                id="reject-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this rejected? Sent to the requester + audit log."
                className="min-h-[100px] rounded border border-input bg-bg p-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <p className="text-xs text-muted-fg">
                Min {REJECT_REASON_MIN} characters.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant={mode === 'reject' ? 'destructive' : 'primary'}
            disabled={
              pending || (mode === 'reject' && reason.trim().length < REJECT_REASON_MIN)
            }
            onClick={submit}
          >
            {pending ? 'Submitting…' : mode === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ModeTabs({
  mode,
  onModeChange,
}: {
  mode: 'approve' | 'reject'
  onModeChange: (mode: 'approve' | 'reject') => void
}) {
  // WAI-ARIA tabs pattern: ←/→ toggles between tabs; focus follows
  // selection (automatic activation — no panel content is heavy
  // enough to need deferred activation). The keydown handler lives
  // on each tab button rather than the tablist so the tablist itself
  // doesn't need to be focusable.
  const approveRef = useRef<HTMLButtonElement>(null)
  const rejectRef = useRef<HTMLButtonElement>(null)

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Home' && e.key !== 'End') {
      return
    }
    e.preventDefault()
    const next: 'approve' | 'reject' =
      e.key === 'Home'
        ? 'approve'
        : e.key === 'End'
          ? 'reject'
          : mode === 'approve'
            ? 'reject'
            : 'approve'
    onModeChange(next)
    ;(next === 'approve' ? approveRef : rejectRef).current?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label="Resolution"
      className="flex gap-1 border-b border-border"
    >
      <ModeTab
        ref={approveRef}
        id="tab-approve"
        active={mode === 'approve'}
        onClick={() => onModeChange('approve')}
        onKeyDown={onKeyDown}
      >
        <CheckCircle2 size={12} aria-hidden /> Approve
      </ModeTab>
      <ModeTab
        ref={rejectRef}
        id="tab-reject"
        active={mode === 'reject'}
        onClick={() => onModeChange('reject')}
        onKeyDown={onKeyDown}
      >
        <ShieldX size={12} aria-hidden /> Reject
      </ModeTab>
    </div>
  )
}

function ModeTab({
  id,
  active,
  onClick,
  onKeyDown,
  children,
  ref,
}: {
  id: string
  active: boolean
  onClick: () => void
  onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => void
  children: React.ReactNode
  ref: React.Ref<HTMLButtonElement>
}) {
  return (
    <button
      ref={ref}
      id={id}
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls="approve-reject-panel"
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={
        '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors ' +
        (active
          ? 'border-primary text-fg font-medium'
          : 'border-transparent text-muted-fg hover:text-fg')
      }
    >
      {children}
    </button>
  )
}
